// Tablero de "Mis tareas" con secciones configurables.
//
// Las columnas se clasifican por regla y además aceptan arrastre manual:
// mover una tarjeta fija esa tarea a esa sección hasta que la muevas otra vez.
// Toda la configuración es personal (users/{uid}/preferences/myTasksBoard).
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { Plus, MoreHorizontal, GripVertical } from 'lucide-react';
import { FiClock, FiFolder, FiRepeat } from 'react-icons/fi';
import { format, isToday, isTomorrow, isPast } from 'date-fns';
import { es } from 'date-fns/locale';
import toast from 'react-hot-toast';
import BoardColumnMenu from './BoardColumnMenu';
import {
    subscribeToBoardConfig, saveBoardConfig, resolveBoard, pruneAssignments,
    getColors, getRuleLabel, newColumnId, DEFAULT_CONFIG,
} from '../services/myTasksBoard';

const RECURRENCE_LABELS = {
    daily: 'Diaria', weekdays: 'L-V', weekly: 'Semanal',
    biweekly: 'Quincenal', monthly: 'Mensual', quarterly: 'Trimestral',
};

const parseDate = (value) => {
    if (!value) return null;
    if (typeof value === 'object' && value.seconds) return new Date(value.seconds * 1000);
    if (typeof value === 'string') { const d = new Date(value); return isNaN(d.getTime()) ? null : d; }
    return null;
};

const formatDate = (value) => {
    const d = parseDate(value);
    if (!d) return '';
    if (isToday(d)) return 'Hoy';
    if (isTomorrow(d)) return 'Mañana';
    return format(d, 'd MMM', { locale: es });
};

const isOverdue = (value) => {
    const d = parseDate(value);
    return !!d && isPast(d) && !isToday(d);
};

const MyTasksBoard = ({
    tasks, users, currentUser, userId,
    onTaskClick, onToggleComplete, onAddTask,
    projectsMap, CompletionCircle, UserAvatar,
}) => {
    const [config, setConfig] = useState(DEFAULT_CONFIG);
    const [menu, setMenu] = useState(null);          // { column, position }
    const [renamingId, setRenamingId] = useState(null);
    const [renameValue, setRenameValue] = useState('');
    const renameRef = useRef(null);

    useEffect(() => {
        if (!userId) return;
        const unsub = subscribeToBoardConfig(userId, setConfig);
        return () => unsub();
    }, [userId]);

    useEffect(() => {
        if (renamingId) setTimeout(() => renameRef.current?.select(), 30);
    }, [renamingId]);

    // Guarda optimista: pinta ya y persiste después. Al guardar asignaciones se
    // limpian las de tareas o columnas que ya no existen.
    const persist = useCallback(async (next) => {
        const { pruned } = pruneAssignments(next, tasks);
        const clean = { ...next, assignments: pruned };
        setConfig(clean);
        try {
            await saveBoardConfig(userId, clean);
        } catch (err) {
            console.error('Error al guardar el tablero:', err);
            toast.error('No se pudo guardar el tablero');
        }
    }, [userId, tasks]);

    const board = useMemo(() => resolveBoard(tasks, config), [tasks, config]);
    const visibleColumns = useMemo(
        () => (config.hideEmpty ? board.filter(c => c.tasks.length > 0) : board),
        [board, config.hideEmpty]
    );

    // ── Arrastre ──
    const handleDragEnd = (result) => {
        const { source, destination, draggableId, type } = result;
        if (!destination) return;

        if (type === 'COLUMN') {
            if (source.index === destination.index) return;
            // Los índices vienen de la lista visible; hay que mapearlos a la real
            // porque puede haber secciones vacías ocultas.
            const movedId = visibleColumns[source.index]?.id;
            const targetId = visibleColumns[destination.index]?.id;
            const from = config.columns.findIndex(c => c.id === movedId);
            const to = config.columns.findIndex(c => c.id === targetId);
            if (from < 0 || to < 0) return;
            const columns = [...config.columns];
            const [moved] = columns.splice(from, 1);
            columns.splice(to, 0, moved);
            persist({ ...config, columns });
            return;
        }

        if (source.droppableId === destination.droppableId) return;

        const task = tasks.find(t => t.id === draggableId);
        if (!task) return;
        const targetColumn = config.columns.find(c => c.id === destination.droppableId);
        if (!targetColumn) return;

        const isDone = task.estado === 'finalizado';

        // Soltar en "Completadas" completa la tarea, y sacarla de ahí la
        // reabre. Sin esto la tarjeta volvería sola a su sitio y parecería roto.
        if (targetColumn.rule === 'done' && !isDone) { onToggleComplete(task); return; }
        if (targetColumn.rule !== 'done' && isDone) onToggleComplete(task);

        persist({ ...config, assignments: { ...config.assignments, [task.id]: targetColumn.id } });
    };

    // ── Acciones del menú ──
    const handleColumnAction = (action, column, value) => {
        const columns = [...config.columns];
        const index = columns.findIndex(c => c.id === column.id);
        if (index < 0 && action !== 'toggle_hide_empty') return;

        switch (action) {
            case 'rename':
                setRenameValue(column.title);
                setRenamingId(column.id);
                return;
            case 'color':
                columns[index] = { ...columns[index], color: value };
                break;
            case 'rule':
                columns[index] = { ...columns[index], rule: value };
                break;
            case 'move_left':
                if (index === 0) return;
                [columns[index - 1], columns[index]] = [columns[index], columns[index - 1]];
                break;
            case 'move_right':
                if (index === columns.length - 1) return;
                [columns[index + 1], columns[index]] = [columns[index], columns[index + 1]];
                break;
            case 'duplicate':
                columns.splice(index + 1, 0, { ...columns[index], id: newColumnId(), title: `${columns[index].title} (copia)`, rule: 'manual' });
                break;
            case 'toggle_hide_empty':
                persist({ ...config, hideEmpty: !config.hideEmpty });
                return;
            case 'delete': {
                if (columns.length <= 1) { toast.error('Debe quedar al menos una sección'); return; }
                const affected = Object.values(config.assignments || {}).filter(id => id === column.id).length;
                const warning = affected > 0
                    ? `\n\n${affected} tarea(s) que habías movido aquí volverán a clasificarse por su fecha.`
                    : '';
                if (!window.confirm(`¿Eliminar la sección "${column.title}"?${warning}\n\nNo se borra ninguna tarea.`)) return;
                columns.splice(index, 1);
                break;
            }
            default:
                return;
        }
        persist({ ...config, columns });
    };

    const commitRename = () => {
        const title = renameValue.trim();
        setRenamingId(null);
        if (!title) return;
        const columns = config.columns.map(c => (c.id === renamingId ? { ...c, title } : c));
        persist({ ...config, columns });
    };

    const handleAddSection = () => {
        const columns = [...config.columns, {
            id: newColumnId(), title: 'Nueva sección', color: 'purple', rule: 'manual',
        }];
        persist({ ...config, columns });
    };

    const openMenu = (e, column) => {
        const rect = e.currentTarget.getBoundingClientRect();
        setMenu({ column, position: { top: rect.bottom, left: rect.left - 200 } });
    };

    return (
        <div className="flex-1 overflow-x-auto p-4 sm:p-6">
            <DragDropContext onDragEnd={handleDragEnd}>
              <div className="flex gap-4 items-start min-w-max">
                <Droppable droppableId="board" direction="horizontal" type="COLUMN">
                    {(boardProvided) => (
                        <div
                            ref={boardProvided.innerRef}
                            {...boardProvided.droppableProps}
                            className="flex gap-4 items-start"
                        >
                            {visibleColumns.map((col, colIndex) => {
                                const colors = getColors(col.color);
                                const isRenaming = renamingId === col.id;

                                return (
                                    <Draggable draggableId={`col-${col.id}`} index={colIndex} key={col.id}>
                                        {(colDrag, colSnap) => (
                                            <div
                                                ref={colDrag.innerRef}
                                                {...colDrag.draggableProps}
                                                className={`w-72 flex-shrink-0 flex flex-col rounded-xl transition-shadow ${colSnap.isDragging ? 'shadow-2xl ring-2 ring-blue-300 bg-white' : ''}`}
                                            >
                                                {/* Cabecera de la sección */}
                                                <div className="flex items-center gap-1.5 mb-3 px-1 group/col">
                                                    <span
                                                        {...colDrag.dragHandleProps}
                                                        className="text-gray-300 hover:text-gray-500 cursor-grab active:cursor-grabbing opacity-0 group-hover/col:opacity-100 transition-opacity"
                                                        title="Arrastra para mover la sección"
                                                    >
                                                        <GripVertical size={14} />
                                                    </span>
                                                    <span className={`w-2 h-2 rounded-full ${colors.dot}`} />

                                                    {isRenaming ? (
                                                        <input
                                                            ref={renameRef}
                                                            value={renameValue}
                                                            onChange={e => setRenameValue(e.target.value)}
                                                            onBlur={commitRename}
                                                            onKeyDown={e => {
                                                                if (e.key === 'Enter') commitRename();
                                                                if (e.key === 'Escape') setRenamingId(null);
                                                            }}
                                                            className="flex-1 min-w-0 text-sm font-semibold text-gray-800 bg-white border border-blue-400 rounded px-1.5 py-0.5 outline-none focus:ring-2 focus:ring-blue-100"
                                                        />
                                                    ) : (
                                                        <h3
                                                            onDoubleClick={() => { setRenameValue(col.title); setRenamingId(col.id); }}
                                                            className="text-sm font-semibold text-gray-700 truncate cursor-text"
                                                            title={`${col.title} · ${getRuleLabel(col.rule)}`}
                                                        >
                                                            {col.title}
                                                        </h3>
                                                    )}

                                                    <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${colors.badge}`}>{col.tasks.length}</span>
                                                    <div className="flex-1" />
                                                    <button
                                                        onClick={() => onAddTask(col)}
                                                        className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors opacity-0 group-hover/col:opacity-100"
                                                        title="Agregar tarea aquí"
                                                    >
                                                        <Plus size={14} />
                                                    </button>
                                                    <button
                                                        onClick={(e) => openMenu(e, col)}
                                                        className="p-1 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors opacity-0 group-hover/col:opacity-100"
                                                        title="Opciones de la sección"
                                                    >
                                                        <MoreHorizontal size={15} />
                                                    </button>
                                                </div>

                                                {/* Tarjetas */}
                                                <Droppable droppableId={col.id} type="TASK">
                                                    {(taskProvided, taskSnap) => (
                                                        <div
                                                            ref={taskProvided.innerRef}
                                                            {...taskProvided.droppableProps}
                                                            className={`space-y-2 flex-1 min-h-[80px] rounded-xl p-1 transition-colors ${taskSnap.isDraggingOver ? `${colors.soft} ring-2 ${colors.ring}` : ''}`}
                                                        >
                                                            {col.tasks.map((task, taskIndex) => (
                                                                <Draggable draggableId={task.id} index={taskIndex} key={task.id}>
                                                                    {(cardDrag, cardSnap) => (
                                                                        <div
                                                                            ref={cardDrag.innerRef}
                                                                            {...cardDrag.draggableProps}
                                                                            {...cardDrag.dragHandleProps}
                                                                            onClick={() => onTaskClick(task)}
                                                                            className={`bg-white border ${colors.border} rounded-xl p-3 cursor-pointer transition-all ${cardSnap.isDragging ? 'shadow-xl rotate-1' : 'hover:shadow-md'}`}
                                                                        >
                                                                            <div className="flex items-start gap-2">
                                                                                <CompletionCircle
                                                                                    estado={task.estado}
                                                                                    onClick={(e) => { e.stopPropagation(); onToggleComplete(task); }}
                                                                                    size={16}
                                                                                />
                                                                                <div className="flex-1 min-w-0">
                                                                                    <p className={`text-[13px] font-medium leading-snug ${task.estado === 'finalizado' ? 'text-gray-400 line-through' : 'text-gray-800'}`}>
                                                                                        {task.titulo}
                                                                                    </p>
                                                                                    {projectsMap[task.projectId || task._projectId] && (
                                                                                        <span className="text-[10px] text-gray-400 flex items-center gap-1 mt-1">
                                                                                            <FiFolder size={9} />
                                                                                            {projectsMap[task.projectId || task._projectId]}
                                                                                        </span>
                                                                                    )}
                                                                                </div>
                                                                                {task._isSubtask && (
                                                                                    <span className="text-[9px] bg-purple-100 text-purple-600 px-1 py-0.5 rounded-full font-medium flex-shrink-0">Sub</span>
                                                                                )}
                                                                            </div>
                                                                            <div className="flex items-center gap-2 mt-2">
                                                                                {task.fechaVencimiento && (
                                                                                    <span className={`text-[11px] flex items-center gap-1 ${isOverdue(task.fechaVencimiento) ? 'text-red-500' : 'text-gray-500'}`}>
                                                                                        <FiClock size={10} />
                                                                                        {formatDate(task.fechaVencimiento)}
                                                                                    </span>
                                                                                )}
                                                                                {task.recurrence && task.recurrence !== 'none' && (
                                                                                    <span className="text-[10px] text-blue-500 flex items-center gap-0.5">
                                                                                        <FiRepeat size={9} />
                                                                                        {RECURRENCE_LABELS[task.recurrence]}
                                                                                    </span>
                                                                                )}
                                                                                {config.assignments?.[task.id] === col.id && (
                                                                                    <span className="text-[9px] text-gray-400" title="Movida aquí manualmente">📌</span>
                                                                                )}
                                                                                <div className="flex-1" />
                                                                                <div className="flex -space-x-1">
                                                                                    {task.asignados?.slice(0, 2).map((email, i) => (
                                                                                        <div key={i} className="border border-white rounded-full">
                                                                                            <UserAvatar email={email} users={users} currentUser={currentUser} size="w-5 h-5" />
                                                                                        </div>
                                                                                    ))}
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                </Draggable>
                                                            ))}
                                                            {taskProvided.placeholder}

                                                            {col.tasks.length === 0 && !taskSnap.isDraggingOver && (
                                                                <button
                                                                    onClick={() => onAddTask(col)}
                                                                    className="w-full text-center py-6 text-gray-400 hover:text-blue-600 text-xs border border-dashed border-gray-200 hover:border-blue-300 rounded-xl transition-colors"
                                                                >
                                                                    + Agregar tarea
                                                                </button>
                                                            )}
                                                        </div>
                                                    )}
                                                </Droppable>
                                            </div>
                                        )}
                                    </Draggable>
                                );
                            })}
                            {boardProvided.placeholder}
                        </div>
                    )}
                </Droppable>

                {/* Agregar sección — fuera del Droppable para no alterar los
                    índices del arrastre horizontal de columnas. */}
                <button
                    onClick={handleAddSection}
                    className="w-56 flex-shrink-0 flex items-center justify-center gap-1.5 py-3 text-[13px] font-medium text-gray-400 hover:text-blue-600 border border-dashed border-gray-200 hover:border-blue-300 hover:bg-blue-50/40 rounded-xl transition-colors"
                >
                    <Plus size={15} /> Agregar sección
                </button>
              </div>
            </DragDropContext>

            {menu && (
                <BoardColumnMenu
                    column={menu.column}
                    position={menu.position}
                    isFirst={config.columns.findIndex(c => c.id === menu.column.id) === 0}
                    isLast={config.columns.findIndex(c => c.id === menu.column.id) === config.columns.length - 1}
                    hideEmpty={config.hideEmpty}
                    onAction={handleColumnAction}
                    onClose={() => setMenu(null)}
                />
            )}
        </div>
    );
};

export default MyTasksBoard;
